import { encodeImageToBlurhash } from "@/helpers/blurhash/encodeImageToBlurhash";
import { DEFAULT_SHORT_MAX_DURATION_SECONDS } from "@/constants/shorts";

export type ShortExtractedMetadata = {
  posterBlob: Blob;
  posterUrl: string;
  blurhash: string;
  duration: number;
  cleanup: () => void;
};

/**
 * Extracts poster frame, blurhash, and duration from a video File or Blob.
 * Pre-computes these assets in the background so they are ready before or during upload.
 */
export async function extractShortPosterAndMetadata(
  file: File | Blob,
): Promise<ShortExtractedMetadata> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;

    video.onloadeddata = () => {
      video.currentTime = Math.min(0.2, (video.duration || 1) / 2);
    };

    video.onseeked = async () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 720;
        canvas.height = video.videoHeight || 1280;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Canvas 2D context is null");
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        let blurhash = "";
        try {
          blurhash = await encodeImageToBlurhash(
            canvas.toDataURL("image/jpeg", 0.3),
          );
        } catch (e) {
          console.warn("Blurhash generation skipped:", e);
        }

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (blob) {
              const posterUrl = URL.createObjectURL(blob);
              resolve({
                posterBlob: blob,
                posterUrl,
                blurhash,
                duration: video.duration || DEFAULT_SHORT_MAX_DURATION_SECONDS,
                cleanup: () => {
                  URL.revokeObjectURL(posterUrl);
                },
              });
            } else {
              reject(new Error("Failed to generate poster blob"));
            }
          },
          "image/jpeg",
          0.85,
        );
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        reject(err);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load video"));
    };
  });
}
